import { Grid, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useLocale, formatCurrencyNoDecimalsIfPossible } from '@repo/i18n';
import { currencyAtom } from '@repo/widget-utils/currencyAtom';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useAtom } from 'ximple';
import { useCallback, type JSX } from 'react';
import useValueCardUsageSummary from 'src/hooks/domain/cart/useValueCardUsageSummary';
import { sumBy } from 'lodash-es';

interface IProps {
    totalPrice: number;
    priceToPayLater: number;
    invertedColorsClass?: Record<string, unknown>;
}

export default function SummaryPayment(props: IProps): JSX.Element {
    const { totalPrice, priceToPayLater } = props;
    const valueCardUsageSummary = useValueCardUsageSummary();

    const priceToPayNow = totalPrice - priceToPayLater;
    const creditsToPayNow = valueCardUsageSummary
        ? sumBy(
              Object.values(valueCardUsageSummary).flatMap((x) => x.usages),
              (x) => x.creditCost / 100,
          )
        : 0;

    return (
        <Grid container direction="column" justifyContent="space-between" wrap="nowrap">
            <Grid container justifyContent={'flex-end'} sx={{ mt: 2 }}>
                <Grid size={10}>
                    <PriceToPayTable
                        totalPrice={totalPrice}
                        priceToPayLater={priceToPayLater}
                        priceToPayNow={priceToPayNow}
                        creditsToPayNow={creditsToPayNow}
                        invertedColorsClass={props.invertedColorsClass}
                    ></PriceToPayTable>
                </Grid>
            </Grid>
        </Grid>
    );
}

function PriceToPayTable(props: {
    totalPrice: number;
    priceToPayLater: number;
    priceToPayNow: number;
    creditsToPayNow: number;
    invertedColorsClass?: Record<string, unknown>;
}) {
    const { t } = useLocale();
    const { locale } = useLocale();
    const [currency] = useAtom(currencyAtom);

    const formatPrice = useCallback(
        (price: number) => {
            return formatCurrencyNoDecimalsIfPossible(locale, currency, price);
        },
        [locale, currency],
    );

    return (
        <>
            <Table aria-label={t.aria_price_to_pay_summary}>
                <TableBody>
                    {props.priceToPayLater > 0 && (
                        <>
                            <PriceToPayTableRow
                                label={t.payNow.toUpperCase()}
                                value={formatPrice(props.priceToPayNow)}
                                invertedColorsClass={props.invertedColorsClass}
                            ></PriceToPayTableRow>
                            <PriceToPayTableRow
                                label={t.payLater.toUpperCase()}
                                value={formatPrice(props.priceToPayLater)}
                                invertedColorsClass={props.invertedColorsClass}
                            ></PriceToPayTableRow>
                        </>
                    )}

                    <PriceToPayTableRow
                        label={t.total.toUpperCase()}
                        value={formatPrice(props.totalPrice)}
                        invertedColorsClass={props.invertedColorsClass}
                    ></PriceToPayTableRow>

                    {props.creditsToPayNow > 0 && (
                        <PriceToPayTableRow
                            label={''}
                            value={`+ ${props.creditsToPayNow} ${t.credit.num(props.creditsToPayNow).toLowerCase()}`}
                            invertedColorsClass={props.invertedColorsClass}
                        ></PriceToPayTableRow>
                    )}
                </TableBody>
            </Table>
        </>
    );
}

function PriceToPayTableRow(props: {
    label: string;
    value: string;
    invertedColorsClass?: Record<string, unknown>;
}) {
    const customizations = useCustomizations();

    return (
        <>
            <TableRow>
                <TableCell component="th" scope="row" align="right" sx={{ p: 0, border: 'none' }}>
                    <Typography
                        variant="h6"
                        align="right"
                        color="textSecondary"
                        sx={props.invertedColorsClass}
                    >
                        {props.label}
                    </Typography>
                </TableCell>
                <TableCell align="right" sx={{ p: 0, border: 'none' }}>
                    <Typography
                        variant="h4"
                        align="right"
                        fontSize={22}
                        fontWeight={customizations.boldFontWeight}
                        color="textSecondary"
                        sx={props.invertedColorsClass}
                    >
                        {props.value}
                    </Typography>
                </TableCell>
            </TableRow>
        </>
    );
}
